import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import "../truthDare.css";
import "./game.css";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../admin/sessionSlice";
import { listQuestions } from "../../admin/functions/questionsApi";
import { listParticipants } from "../../admin/functions/participantsApi";
import { normalizeTruthDareCategory } from "../../functions/truthDareCategories";
import { bootstrapStageAndReport } from "../../functions/stageReportSync";
import { setBackButtonUrl } from "../uiSlice";
import useFitText from "../useFitText";
import { computeSeatLayout } from "./seatLayout";
import bottleImg from "./img/bottle.png";
import truthImg from "./img/truth.png";
import dareImg from "./img/dare.png";
import avatar1 from "./img/avatars/1.png";
import avatar2 from "./img/avatars/2.png";
import avatar3 from "./img/avatars/3.png";
import avatar4 from "./img/avatars/4.png";
import avatar5 from "./img/avatars/5.png";
import avatar6 from "./img/avatars/6.png";
import avatar7 from "./img/avatars/7.png";
import avatar8 from "./img/avatars/8.png";
import avatar9 from "./img/avatars/9.png";
import avatar10 from "./img/avatars/10.png";

const MIN_PLAYERS = 4;
const DEMO_SPIN_LIMIT = 3;
const MOBILE_QUERY = "(max-width: 768px)";

// Avatars img/1.png … img/10.png from old/game.php, reused in order past ten players.
const AVATARS = [
  avatar1, avatar2, avatar3, avatar4, avatar5,
  avatar6, avatar7, avatar8, avatar9, avatar10,
];

function getStoredUser(authUser) {
  if (authUser && typeof authUser === "object") return authUser;
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}") || {};
  } catch {
    return {};
  }
}

function isDemoUser(user) {
  const role = String(user.role || "").toLowerCase();
  const source = String(user.source || "").toUpperCase();
  const sid = String(user.sessionId || "");
  return sid === "demobypass" || role === "demobypass" || source === "DEMO" || sid.startsWith("demo");
}

function readViewport() {
  return {
    width: window.innerWidth,
    height: window.innerHeight,
    isMobile: window.matchMedia(MOBILE_QUERY).matches,
  };
}

function useViewport() {
  const [viewport, setViewport] = useState(readViewport);
  useEffect(() => {
    const onResize = () => setViewport(readViewport());
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);
  return viewport;
}

/** Which player the bottle points at after rotating `rotation` degrees (old/game.php formula). */
function pickPlayerIndex(rotation, playerCount) {
  const normalized = rotation % 360;
  const segment = 360 / playerCount;
  return Math.floor((360 - normalized + segment / 2) / segment) % playerCount;
}

/** Spin-the-bottle game from old/game.php: spin, pick a player, choose Truth or Dare. */
export default function Game() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const viewport = useViewport();
  const controlsRef = useRef(null);
  const pageRef = useRef(null);
  // Space taken by the player header (top) and the status/Start bar (bottom).
  const [reserves, setReserves] = useState({ top: 90, bottom: 100 });

  const { user } = useSelector((s) => s.auth);
  const { data: themeData, currentTheme } = useSelector((s) => s.theme);
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const storedUser = useMemo(() => getStoredUser(user), [user]);

  const [players, setPlayers] = useState(null); // null = loading
  const [questions, setQuestions] = useState([]);
  const [rotation, setRotation] = useState(0);
  const [spinDuration, setSpinDuration] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [step, setStep] = useState(null); // "player" | "choice" | "question"
  const [question, setQuestion] = useState({ category: "", text: "" });
  const spinCountRef = useRef(0);
  const spinTimerRef = useRef(null);
  const questionRef = useRef(null);

  // Long questions (up to 350 chars) shrink to fit the bubble instead of scrolling.
  useFitText(questionRef, [step, question.text, viewport.width, viewport.height], { min: 0.6 });

  useEffect(() => {
    dispatch(setBackButtonUrl("/rules"));
  }, [dispatch]);

  useEffect(() => () => window.clearTimeout(spinTimerRef.current), []);

  // Keep the circle clear of the header and the controls bar, re-measured on resize.
  useLayoutEffect(() => {
    const header = document.querySelector(".upperaction");
    const top = (header?.getBoundingClientRect().bottom || 80) + 10;
    const controlsTop = controlsRef.current?.getBoundingClientRect().top;
    const bottom = controlsTop ? viewport.height - controlsTop + 12 : 100;
    setReserves((prev) =>
      Math.abs(prev.top - top) < 1 && Math.abs(prev.bottom - bottom) < 1 ? prev : { top, bottom }
    );
  }, [viewport, players]);

  // Registers the player's stat row and report, as the old player flow did on entering play.
  useEffect(() => {
    bootstrapStageAndReport({
      backendBase: String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, ""),
      adminToken,
      storedUser,
      isDemoBypass: isDemoUser(storedUser),
    });
  }, [adminToken, storedUser]);

  useEffect(() => {
    if (!currentTheme || !adminToken) return undefined;
    let cancelled = false;
    Promise.all([
      listParticipants({ themeName: currentTheme, token: adminToken }),
      listQuestions({
        themeName: currentTheme,
        organizationId: organizationId || "admin",
        sessionId: sessionId || "admin",
        token: adminToken,
      }),
    ])
      .then(([participantRows, questionRows]) => {
        if (cancelled) return;
        setPlayers(
          participantRows.map((p) => `${p.first_name || ""} ${p.last_name || ""}`.trim())
        );
        setQuestions(questionRows);
      })
      .catch((err) => {
        console.error("Game load:", err);
        if (!cancelled) setPlayers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [currentTheme, adminToken, organizationId, sessionId]);

  useEffect(() => {
    if (players === null || players.length >= MIN_PLAYERS) return;
    Swal.fire({
      icon: "warning",
      title: "Oops!",
      text: `Please add a minimum of ${MIN_PLAYERS} participants.`,
      confirmButtonText: "OK",
    }).then(() => navigate("/rules"));
  }, [players, navigate]);

  const spin = () => {
    if (spinning || !players?.length) return;
    spinCountRef.current += 1;
    if (spinCountRef.current > DEMO_SPIN_LIMIT && isDemoUser(storedUser)) {
      window.location.href = `${process.env.REACT_APP_BASE_URL}/plans`;
      return;
    }

    const spins = Math.floor(Math.random() * 5) + 5; // 5 to 9 full turns
    const total = rotation + spins * 360 + Math.random() * 360;
    const duration = Math.random() * 2 + 3; // 3 to 5 seconds

    setSelectedIndex(null);
    setSpinning(true);
    setSpinDuration(duration);
    setRotation(total);
    spinTimerRef.current = window.setTimeout(() => {
      setSpinning(false);
      setSelectedIndex(pickPlayerIndex(total, players.length));
      setStep("player");
    }, duration * 1000);
  };

  const chooseCategory = (raw) => {
    const category = normalizeTruthDareCategory(raw);
    const pool = questions
      .filter((q) => normalizeTruthDareCategory(q.category) === category)
      .map((q) => String(q.question_name ?? "").trim())
      .filter(Boolean);
    setQuestion({
      category,
      text: pool.length
        ? pool[Math.floor(Math.random() * pool.length)]
        : `No questions available for ${category.toLowerCase()}.`,
    });
    setStep("question");
  };

  const layout = computeSeatLayout({
    names: players || [],
    count: players?.length || 1,
    width: viewport.width,
    height: viewport.height,
    topReserve: reserves.top,
    bottomReserve: reserves.bottom,
    isMobile: viewport.isMobile,
  });
  const nameFontsKey = layout.nameFonts.join(",");
  // A wrapped block is as wide as its container, so the pill background would cover empty space
  // beside the text. Shrink each wrapped pill to its longest line (measured, so it is exact).
  useLayoutEffect(() => {
    const fitPills = () => {
      pageRef.current?.querySelectorAll(".td-seat-name").forEach((pill) => {
        const text = pill.firstElementChild;
        if (!text) return;
        pill.style.width = "";
        const range = document.createRange();
        range.selectNodeContents(text);
        const lines = Array.from(range.getClientRects());
        if (lines.length < 2) return; // a single line already hugs its text
        const style = getComputedStyle(pill);
        const padX = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
        pill.style.width = `${Math.ceil(Math.max(...lines.map((r) => r.width)) + padX) + 1}px`;
      });
    };
    fitPills();
    document.fonts?.ready.then(fitPills); // re-fit once the display font has loaded
  }, [players, layout.slot, nameFontsKey, selectedIndex]);

  const tableSize = Math.max(layout.radius * 0.72, 90);
  const bottleWidth = Math.max(layout.radius * 0.12, 18);
  const selectedName = selectedIndex == null ? "" : players?.[selectedIndex] || "No Player";
  const hasSpun = spinCountRef.current > 0;

  let status = "Tap the bottle or press Start to spin!";
  if (spinning) status = "Spinning… who will it be?";
  else if (selectedName) status = `${selectedName}'s turn!`;

  return (
    <div ref={pageRef} className={`td-game-page${spinning ? " is-spinning" : ""}`}>
      {players?.map((name, i) => {
        const { x, y } = layout.seats[i];
        return (
          <div
            className={`td-seat${selectedIndex === i ? " is-selected" : ""}`}
            key={i}
            style={{
              left: x - layout.slot / 2,
              top: y - layout.avatar / 2,
              width: layout.slot,
            }}
          >
            <img
              src={AVATARS[i % AVATARS.length]}
              alt=""
              style={{ width: layout.avatar, height: layout.avatar }}
            />
            <p
              className="td-seat-name"
              style={{ color: themeData?.sub_color, fontSize: layout.nameFonts[i] }}
            >
              <span>{name}</span>
            </p>
          </div>
        );
      })}

      <div
        className="td-table"
        aria-hidden
        style={{ left: layout.cx, top: layout.cy, width: tableSize, height: tableSize }}
      />
      <button
        type="button"
        className="td-bottle"
        style={{ left: layout.cx, top: layout.cy, width: bottleWidth }}
        onClick={spin}
        disabled={spinning}
        aria-label="Spin the bottle"
      >
        <img
          src={bottleImg}
          alt=""
          style={{
            transform: `rotate(${rotation}deg)`,
            transition: spinDuration ? `transform ${spinDuration}s cubic-bezier(0.15, 0.8, 0.25, 1)` : "none",
          }}
        />
      </button>

      <div className="td-controls" ref={controlsRef}>
        {players?.length ? (
          <div className="td-status" aria-live="polite">
            {status}
          </div>
        ) : null}
        <button
          type="button"
          className={`td-cta td-start-btn${hasSpun ? "" : " td-cta--pulse"}`}
          onClick={spin}
          disabled={spinning || !players?.length}
        >
          <i className={`fa-solid fa-rotate-right${spinning ? " fa-spin" : ""}`} aria-hidden />
          {spinning ? "Spinning…" : hasSpun ? "Spin Again" : "Start"}
        </button>
      </div>

      {step === "player" && (
        <div className="td-modal-backdrop">
          <div className="td-card td-card--player" role="dialog" aria-modal="true" aria-labelledby="td-player-name">
            <div className="td-card-band">
              <span className="td-card-burst">It&apos;s your turn!</span>
            </div>
            <img className="td-card-avatar" src={AVATARS[(selectedIndex ?? 0) % AVATARS.length]} alt="" />
            <div className="td-card-body">
              <h3 id="td-player-name" className="td-card-name">
                {selectedName}
              </h3>
              <p className="td-card-hint">Truth or Dare, it&apos;s your call.</p>
              <button type="button" className="td-cta" onClick={() => setStep("choice")}>
                Let&apos;s go <i className="fa-solid fa-angles-right" aria-hidden />
              </button>
            </div>
          </div>
        </div>
      )}

      {step === "choice" && (
        <div className="td-modal-backdrop" onClick={() => setStep(null)}>
          <div
            className="td-modal td-choice-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Truth or Dare"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="td-choice-title">{selectedName}, pick one!</p>
            <div className="td-choice-cards">
              <button type="button" className="td-choice-btn" onClick={() => chooseCategory("truth")}>
                <img src={truthImg} alt="Truth" />
              </button>
              <span className="td-choice-or" aria-hidden>
                OR
              </span>
              <button type="button" className="td-choice-btn" onClick={() => chooseCategory("dare")}>
                <img src={dareImg} alt="Dare" />
              </button>
            </div>
          </div>
        </div>
      )}

      {step === "question" && (
        <div className="td-modal-backdrop">
          <div
            className={`td-card td-card--question is-${question.category.toLowerCase()}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="td-question-category"
          >
            <div className="td-card-band">
              <img
                className="td-card-band-art"
                src={question.category === "Dare" ? dareImg : truthImg}
                alt=""
              />
              <span id="td-question-category" className="td-card-burst td-card-burst--big">
                {question.category}
              </span>
            </div>
            <div className="td-card-body">
              <p className="td-card-for">
                <img src={AVATARS[(selectedIndex ?? 0) % AVATARS.length]} alt="" />
                {selectedName}
              </p>
              <p className="td-card-question" ref={questionRef}>
                {question.text}
              </p>
              <button type="button" className="td-cta" onClick={() => setStep(null)}>
                <i className="fa-solid fa-check" aria-hidden /> Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
